import React from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  ArrowDownToLine, ArrowUpFromLine, CheckCircle2, XCircle,
  Clock, Loader2, Eye, Copy, Check,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getMerchantName } from "@/shared/utils";

/* ── Unified Design Tokens ──────────────────────────────────────── */
const C = {
  bg: "#060C18", card: "#0E1C35", cardHigh: "#122040", hover: "#162847",
  border: "rgba(255,255,255,0.055)", borderMid: "rgba(255,255,255,0.09)",
  cyan: "#22D3EE", cyanBright: "#67E8F9", cyanSoft: "rgba(34,211,238,0.08)",
  cyanGlow: "rgba(34,211,238,0.18)",
  blue: "#3B82F6", blueSoft: "rgba(59,130,246,0.10)",
  emerald: "#10B981", emeraldSoft: "rgba(16,185,129,0.10)",
  amber: "#FBBF24", amberSoft: "rgba(251,191,36,0.08)",
  rose: "#FB7185", roseSoft: "rgba(251,113,133,0.10)",
  t1: "#EFF6FF", t2: "#93B4D4", t3: "#4D6E90", t4: "#2A4060",
};

const statusConfigDefault = {
  pending: { icon: Clock, darkColor: "rgba(251,191,36,0.12)", darkBorder: "rgba(251,191,36,0.28)", darkText: "#FBBF24", label: "Pending" },
  processing: { icon: Loader2, darkColor: "rgba(59,130,246,0.12)", darkBorder: "rgba(59,130,246,0.28)", darkText: "#60A5FA", label: "Processing" },
  success: { icon: CheckCircle2, darkColor: "rgba(16,185,129,0.12)", darkBorder: "rgba(16,185,129,0.28)", darkText: "#34D399", label: "Success" },
  failed: { icon: XCircle, darkColor: "rgba(251,113,133,0.12)", darkBorder: "rgba(251,113,133,0.28)", darkText: "#FB7185", label: "Failed" },
  expired: { icon: Clock, darkColor: "rgba(71,85,105,0.18)", darkBorder: "rgba(71,85,105,0.30)", darkText: "#64748B", label: "Expired" },
  cancelled: { icon: XCircle, darkColor: "rgba(71,85,105,0.18)", darkBorder: "rgba(71,85,105,0.30)", darkText: "#64748B", label: "Cancelled" },
  declined: { icon: XCircle, darkColor: "rgba(251,113,133,0.12)", darkBorder: "rgba(251,113,133,0.28)", darkText: "#FB7185", label: "Declined" },
};

const getStatusConfig = (t) => {
  if (!t) return statusConfigDefault;
  return {
    pending: { ...statusConfigDefault.pending, label: t("pending") },
    processing: { ...statusConfigDefault.processing, label: t("processing") },
    success: { ...statusConfigDefault.success, label: t("success") },
    failed: { ...statusConfigDefault.failed, label: t("failed") },
    expired: { ...statusConfigDefault.expired, label: t("expired") },
    cancelled: { ...statusConfigDefault.cancelled, label: t("cancelled") },
    declined: { ...statusConfigDefault.declined, label: t("declined") || "Declined" },
  };
};

/* ── Scoped Styles ──────────────────────────────────────────────── */
const ATT_CSS = `
  .att-root {
    font-family: 'Outfit', system-ui, sans-serif;
    background: linear-gradient(155deg, #122040, #0E1C35);
    border: 1px solid rgba(255,255,255,0.055);
    border-radius: 1rem; overflow: hidden; transition: border-color .25s;
  }
  .att-root:hover { border-color: rgba(34,211,238,0.16); }

  .att-scroll { overflow-x: auto; }
  .att-scroll::-webkit-scrollbar { height: 5px; }
  .att-scroll::-webkit-scrollbar-track { background: transparent; }
  .att-scroll::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.18); border-radius: 99px; }

  .att-table { width: 100%; border-collapse: collapse; min-width: 760px; font-family: 'Outfit', system-ui, sans-serif; }

  .att-thead-row { background: rgba(255,255,255,0.025); border-bottom: 1px solid rgba(255,255,255,0.06); }
  .att-th {
    padding: 11px 16px; font-size: 10px; font-weight: 800; color: #3E5E7E;
    text-transform: uppercase; letter-spacing: .14em; white-space: nowrap; text-align: left;
  }

  .att-tbody-row { border-bottom: 1px solid rgba(255,255,255,0.04); transition: background .16s; cursor: pointer; }
  .att-tbody-row:last-child { border-bottom: none; }
  .att-tbody-row:hover { background: rgba(34,211,238,0.045); }
  .att-tbody-row:hover .att-first-td { border-left: 3px solid #22D3EE; }

  .att-td { padding: 13px 16px; font-size: 13px; font-weight: 500; vertical-align: middle; color: #93B4D4; transition: color .16s; }
  .att-tbody-row:hover .att-td { color: #EFF6FF; }

  .att-type-icon {
    width: 2rem; height: 2rem; border-radius: 0.5rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    border: 1px solid transparent; transition: box-shadow .2s, border-color .2s;
  }
  .att-type-icon--dep { background: rgba(59,130,246,0.10); border-color: rgba(59,130,246,0.18); }
  .att-type-icon--dep svg { color: #60A5FA; width: .9rem; height: .9rem; }
  .att-type-icon--wd  { background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.18); }
  .att-type-icon--wd svg { color: #FBBF24; width: .9rem; height: .9rem; }
  .att-tbody-row:hover .att-type-icon--dep { border-color: rgba(59,130,246,0.4); box-shadow: 0 0 8px rgba(59,130,246,0.15); }
  .att-tbody-row:hover .att-type-icon--wd  { border-color: rgba(251,191,36,0.4);  box-shadow: 0 0 8px rgba(251,191,36,0.15); }

  .att-id-mono { font-family: ui-monospace, monospace; font-size: .75rem; color: #93B4D4; transition: color .16s; }
  .att-tbody-row:hover .att-id-mono { color: #22D3EE; }

  .att-copy-btn {
    background: none; border: none; cursor: pointer; padding: .15rem; border-radius: .25rem;
    display: flex; align-items: center; opacity: 0; transition: opacity .16s, background .16s;
  }
  .att-tbody-row:hover .att-copy-btn { opacity: 1; }
  .att-copy-btn:hover { background: rgba(34,211,238,0.12); }
  .att-copy-btn svg { width: .7rem; height: .7rem; color: #4D6E90; transition: color .16s; }
  .att-copy-btn:hover svg { color: #22D3EE; }

  .att-badge-type {
    display: inline-block; padding: .15rem .45rem; border-radius: .4rem;
    font-size: .68rem; font-weight: 700;
    background: rgba(34,211,238,0.10); border: 1px solid rgba(34,211,238,0.22); color: #22D3EE;
  }

  .att-provider-tag {
    display: inline-block; padding: .2rem .5rem; border-radius: .375rem;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
    font-size: .75rem; color: #93B4D4; transition: border-color .16s, color .16s;
  }
  .att-tbody-row:hover .att-provider-tag { border-color: rgba(34,211,238,0.22); color: #EFF6FF; }

  .att-amount-dep { font-weight: 700; color: #60A5FA; }
  .att-amount-wd  { font-weight: 700; color: #FBBF24; }

  .att-date-main { font-size: .8rem; color: #93B4D4; }
  .att-date-time { font-size: .7rem; color: #4D6E90; font-family: ui-monospace, monospace; }
  .att-tbody-row:hover .att-date-main { color: #EFF6FF; }

  .att-action-btn {
    width: 1.75rem; height: 1.75rem; border-radius: .45rem;
    border: 1px solid rgba(34,211,238,0.18); background: rgba(34,211,238,0.07);
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: background .18s, border-color .18s, box-shadow .18s;
  }
  .att-action-btn svg { width: .85rem; height: .85rem; color: #22D3EE; }
  .att-action-btn:hover { background: rgba(34,211,238,0.14); border-color: rgba(34,211,238,0.38); box-shadow: 0 0 12px rgba(34,211,238,0.18); }

  .att-status-badge { display: inline-flex; align-items: center; gap: .3rem; padding: .2rem .55rem; border-radius: 9999px; font-size: .68rem; font-weight: 700; border: 1px solid transparent; }
  .att-status-icon { width: .7rem; height: .7rem; flex-shrink: 0; }

  .att-reseller-name { font-weight: 600; font-size: .8125rem; color: #B8D4EE; }
  .att-direct-label  { font-size: .75rem; color: #4D6E90; }
  .att-merchant-name { font-weight: 600; font-size: .8375rem; color: #B8D4EE; }

  .att-empty { padding: 3rem 1rem; text-align: center; color: #4D6E90; font-size: .875rem; }

  .att-loading { padding: 2rem 1.5rem; display: flex; flex-direction: column; gap: .875rem; }
  .att-skeleton-row { display: flex; align-items: center; gap: .875rem; }
  .att-sk { border-radius: .375rem; background: linear-gradient(90deg,#21262d 25%,#30363d 50%,#21262d 75%); background-size: 200% 100%; animation: att-shimmer 1.5s infinite; }
  @keyframes att-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .att-sk-icon  { width:2rem; height:2rem; border-radius:.5rem; flex-shrink:0 }
  .att-sk-lg    { height:.875rem; flex:2; max-width:160px }
  .att-sk-md    { height:.875rem; flex:1.5; max-width:110px }
  .att-sk-sm    { height:.875rem; flex:1; max-width:80px }
  .att-sk-pill  { height:1.5rem; width:4.5rem; border-radius:9999px }
`;

export default function AdminTransactionTable({
  transactions,
  isLoading,
  onView,
  showMerchant = true,
  showReseller = true,
  showEngine = false,
  compact = false,
  merchants = [],
  resellers = [],
  engines = [],
  isQRCode = false,
  mixedTypes = false,
}) {
  const [copiedId, setCopiedId] = React.useState(null);

  let t = null;
  let language = "en";
  try {
    const langContext = useLanguage();
    t = langContext?.t;
    language = langContext?.language || "en";
  } catch (e) { }

  const statusConfig = getStatusConfig(t);

  const getMerchantNameForTx = (tx) => {
    if (tx.merchant) return getMerchantName(tx.merchant, language, t);
    if (tx.merchant_name) return getMerchantName({ name: tx.merchant_name }, language, t);
    const merchant = merchants.find(m => m.id === tx.merchant_id || m.code === tx.merchant_code);
    if (merchant) return getMerchantName(merchant, language, t);
    return tx.merchant_code || "—";
  };

  const getResellerName = (tx) => {
    if (tx.reseller) return tx.reseller.name;
    if (tx.merchant?.createdByResellerId && resellers.length > 0) {
      const r = resellers.find(res => res.id === tx.merchant.createdByResellerId);
      return r?.name || null;
    }
    if (tx.reseller_name) return tx.reseller_name;
    const reseller = resellers.find(r => r.id === tx.reseller_id || r.code === tx.reseller_code);
    return reseller?.name || null;
  };

  const getEngineName = (engineId) => {
    const engine = engines.find(e => e.id === engineId || e.code === engineId);
    return engine?.name || engineId || "—";
  };

  const getTransactionTypeLabel = (type) => {
    if (!t) return type;
    const typeUpper = (type || "").toUpperCase();
    if (typeUpper === "DEPOSIT") return t("deposit");
    if (typeUpper === "WITHDRAWAL") return t("withdrawal");
    return type;
  };

  const copyId = (id, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return (
      <>
        <style>{ATT_CSS}</style>
        <div className="att-root">
          <div className="att-loading">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="att-skeleton-row" style={{ animationDelay: `${i * 60}ms`, opacity: 0, animation: `att-shimmer .4s ${i * 60}ms forwards` }}>
                <div className="att-sk att-sk-icon" />
                <div className="att-sk att-sk-lg" />
                <div className="att-sk att-sk-md" />
                <div className="att-sk att-sk-sm" />
                <div className="att-sk att-sk-pill" />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (!transactions || transactions.length === 0) {
    return (
      <>
        <style>{ATT_CSS}</style>
        <div className="att-root">
          <div className="att-empty">{t ? t("noTransactionsFound") : "No transactions found"}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{ATT_CSS}</style>
      <div className="att-root">
        <div className="att-scroll">
          <table className="att-table">
            <thead>
              <tr className="att-thead-row">
                <th className="att-th">{t ? t("transaction") : "Transaction"}</th>
                {showEngine && <th className="att-th">{t ? t("engine") : "Engine"}</th>}
                {showReseller && <th className="att-th">{t ? t("reseller") : "Reseller"}</th>}
                {showMerchant && <th className="att-th">{t ? t("merchant") : "Merchant"}</th>}
                <th className="att-th">{t ? t("provider") : "Provider"}</th>
                <th className="att-th" style={{ textAlign: "right" }}>{t ? t("netAmount") : "Net Amount"}</th>
                <th className="att-th" style={{ textAlign: "right" }}>{t ? t("totalFee") || "Total Fee" : "Total Fee"}</th>
                <th className="att-th">{t ? t("status") : "Status"}</th>
                <th className="att-th">{t ? t("date") : "Date"}</th>
                <th className="att-th" style={{ width: "3rem" }} />
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, index) => {
                const statusKey = (tx.status || "").toLowerCase();
                const status = statusConfig[statusKey] || statusConfig.pending;
                const StatusIcon = status.icon;
                const txUpperType = (tx.type || "").toUpperCase();
                const isWithdrawal = txUpperType === "WITHDRAWAL";
                const isQrCodeTx = isQRCode || tx.is_qr_code;
                const displayId = isWithdrawal
                  ? (tx.transaction_id || tx.id)
                  : (isQrCodeTx ? (tx.payment_id || tx.transaction_id || tx.id) : (tx.transaction_id || tx.id));

                return (
                  <motion.tr
                    key={tx.id}
                    className="att-tbody-row"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(index * 0.018, 0.3) }}
                    onClick={() => onView(tx)}
                  >
                    {/* ID */}
                    <td className="att-td att-first-td">
                      <div style={{ display: "flex", alignItems: "center", gap: ".625rem" }}>
                        <div className={`att-type-icon ${isWithdrawal ? "att-type-icon--wd" : "att-type-icon--dep"}`}>
                          {isWithdrawal ? <ArrowUpFromLine /> : <ArrowDownToLine />}
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: ".35rem" }}>
                            <span className="att-id-mono">{(displayId?.slice(0, 14) || displayId) || "—"}</span>
                            <button className="att-copy-btn" onClick={(e) => copyId(displayId, e)}>
                              {copiedId === displayId
                                ? <Check style={{ width: ".7rem", height: ".7rem", color: "#34D399" }} />
                                : <Copy />}
                            </button>
                          </div>
                          {tx.type && (
                            <span className="att-badge-type">{getTransactionTypeLabel(tx.type)}</span>
                          )}
                          {/* {isQrCodeTx && (tx.reference || tx.reference_id) && (
                            <span className="att-badge-type" style={{ marginLeft: tx.type ? 6 : 0 }}>{tx.reference || tx.reference_id}</span>
                          )} */}
                        </div>
                      </div>
                    </td>

                    {showEngine && (
                      <td className="att-td">
                        <span className="att-reseller-name">
                          {engines.length > 0 ? getEngineName(tx.engine_id || tx.engine_code) : (tx.engine_code || "—")}
                        </span>
                      </td>
                    )}

                    {showReseller && (
                      <td className="att-td">
                        {(() => {
                          const name = getResellerName(tx);
                          return name
                            ? <span className="att-reseller-name">{name}</span>
                            : <span className="att-direct-label">{t ? t("direct") : "Direct"}</span>;
                        })()}
                      </td>
                    )}

                    {showMerchant && (
                      <td className="att-td">
                        <span className="att-merchant-name">{getMerchantNameForTx(tx)}</span>
                      </td>
                    )}

                    {/* Provider */}
                    <td className="att-td">
                      <span className="att-provider-tag">
                        {(() => {
                          const fallback = t ? t("nA") : "N/A";
                          const raw = isWithdrawal
                            ? (tx.client_bank_name || tx.bank_name || tx.bank_code || tx.provider_display || fallback)
                            : (isQrCodeTx
                              ? (tx.client_bank_name || tx.provider_display || tx.provider_code || fallback)
                              : (tx.provider_display || tx.provider_code || tx.client_bank_name || tx.bank_name || tx.bank_code || fallback));
                          if (raw === "QR Code" && t) return t("qrCode");
                          return raw;
                        })()}
                      </span>
                    </td>

                    {/* Amount (Net) */}
                    <td className="att-td" style={{ textAlign: "right" }}>
                      <span className={isWithdrawal ? "att-amount-wd" : "att-amount-dep"}>
                        {isWithdrawal ? "-" : "+"}฿{(tx.net_amount ?? tx.amount ?? 0).toLocaleString()}
                      </span>
                    </td>

                    {/* Total Fee */}
                    <td className="att-td" style={{ textAlign: "right" }}>
                      <span style={{ color: C.t2, fontWeight: 600 }}>
                        ฿{(tx.total_fee ?? tx.fee_amount ?? 0).toLocaleString()}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="att-td">
                      <span className="att-status-badge" style={{
                        background: status.darkColor,
                        borderColor: status.darkBorder,
                        color: status.darkText,
                      }}>
                        <StatusIcon className={`att-status-icon ${statusKey === "processing" ? "animate-spin" : ""}`} />
                        {status.label}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="att-td">
                      <div>
                        <div className="att-date-main">
                          {(tx.created_date || tx.createdAt)
                            ? format(new Date(tx.created_date || tx.createdAt), compact ? "MM/dd HH:mm" : "MMM d, yyyy")
                            : "N/A"}
                        </div>
                        <div className="att-date-time">
                          {(tx.created_date || tx.createdAt)
                            ? format(new Date(tx.created_date || tx.createdAt), "HH:mm:ss")
                            : ""}
                        </div>
                      </div>
                    </td>

                    {/* Action */}
                    <td className="att-td">
                      <button className="att-action-btn" onClick={(e) => { e.stopPropagation(); onView(tx); }}>
                        <Eye />
                      </button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}